import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  FileText,
  AlertCircle,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import SortableTableHeader from '../../components/SortableTableHeader';
import TransactionListTotalsBar from '../../components/transactions/TransactionListTotalsBar';
import RowActionsMenu from '../../components/transactions/RowActionsMenu';
import QuotationSendModal from '../../components/QuotationSendModal';
import {
  getQuotations,
  deleteQuotation,
  copyQuotation,
  convertQuotationToInvoice,
  downloadQuotationPdf,
} from '../../utils/sales/quotationApi';
import { printQuotation } from '../../utils/sales/printQuotation';
import { canManageQuotations, canConvertQuotationToInvoice } from '../../utils/salesTransactionPermissions';
import { buildQuotationRowActions, canDeleteQuotations } from '../../utils/quotationRowActions';
import {
  getQuotationCustomerName,
  type Quotation,
  type QuotationStatus,
  type GetQuotationsParams,
} from '../../types/sales/quotation';
import type { PaginationMeta } from '../../types/journalEntry';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast, toastRouteSuccess, clearRouteState } from '../../utils/toast';
import { todayLocalIso, daysAgoLocalIso } from '../../utils/localDate';
import { computeQuotationTotals, quotationRowKey } from '../../utils/quotationSelection';

type SortField = 'quotation_number' | 'quote_date' | 'total_amount' | 'status';
type SortDir = 'asc' | 'desc';

type StatusFilter = 'all' | QuotationStatus;

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'DRAFT', label: 'Draft' },
  { id: 'SENT', label: 'Sent' },
  { id: 'ACCEPTED', label: 'Accepted' },
  { id: 'REJECTED', label: 'Rejected' },
  { id: 'EXPIRED', label: 'Expired' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString();

const getStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'SENT':
      return 'bg-blue-100 text-blue-800';
    case 'ACCEPTED':
      return 'bg-green-100 text-green-800';
    case 'REJECTED':
      return 'bg-red-100 text-red-700';
    case 'EXPIRED':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const openQuotation = (id: number) => `/quotations/manage?id=${id}`;

const defaultSortDir = (field: SortField): SortDir =>
  field === 'quote_date' || field === 'total_amount' ? 'desc' : 'asc';

const toApiSort = (field: SortField, dir: SortDir): string =>
  dir === 'desc' ? `-${field}` : field;

const Quotations = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendQuotationRow, setSendQuotationRow] = useState<Quotation | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [startDate, setStartDate] = useState(() => daysAgoLocalIso(90));
  const [endDate, setEndDate] = useState(() => todayLocalIso());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [sortField, setSortField] = useState<SortField>('quote_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const state = location.state as { successMessage?: string } | null;
    toastRouteSuccess(state);
    if (state?.successMessage) {
      clearRouteState();
    }
  }, [location.state]);

  const fetchQuotations = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);

      const params: GetQuotationsParams = {
        sort: toApiSort(sortField, sortDir),
        per_page: perPage,
        page: pageNum,
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (startDate) params.quote_date_from = startDate;
      if (endDate) params.quote_date_to = endDate;

      const result = await getQuotations(params);
      setQuotations(result.quotations);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch quotations');
      setQuotations([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, startDate, endDate, searchQuery, perPage, sortField, sortDir]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const handleSort = (column: string) => {
    const col = column as SortField;
    if (sortField === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(col);
      setSortDir(defaultSortDir(col));
    }
    setPage(1);
  };

  const handleDelete = async (q: Quotation) => {
    if (!canDeleteQuotations()) return;
    if (
      !window.confirm(
        `Delete quotation ${q.quotation_number}? This cannot be undone.`
      )
    )
      return;

    try {
      setActionLoading(q.id);
      await deleteQuotation(q.id);
      toast.success(`Quotation ${q.quotation_number} deleted.`);
      await fetchQuotations(page);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete quotation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopy = async (q: Quotation) => {
    if (!canManageQuotations()) return;
    if (
      !window.confirm(
        `Copy quotation ${q.quotation_number}? A new draft will be created.`
      )
    )
      return;

    try {
      setActionLoading(q.id);
      const copied = await copyQuotation(q.id);
      toast.success(`Copied to ${copied.quotation_number}.`);
      navigate(openQuotation(copied.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to copy quotation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConvertToInvoice = async (q: Quotation) => {
    if (!canConvertQuotationToInvoice()) return;
    if (
      !window.confirm(
        `Convert quotation ${q.quotation_number} to a draft invoice? Tax will be recalculated. The quotation will not be changed.`
      )
    )
      return;

    try {
      setActionLoading(q.id);
      const invoice = await convertQuotationToInvoice(q.id);
      toast.success(`Created invoice ${invoice.invoice_number}.`);
      navigate(`/manage-sales?id=${invoice.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to convert quotation to invoice');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async (q: Quotation) => {
    try {
      setActionLoading(q.id);
      await downloadQuotationPdf(q.id, `${q.quotation_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download quotation PDF');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = async (q: Quotation) => {
    try {
      setActionLoading(q.id);
      await printQuotation(q);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print quotation');
    } finally {
      setActionLoading(null);
    }
  };

  const openSendModal = (q: Quotation) => {
    if (!canManageQuotations()) return;
    setSendQuotationRow(q);
    setShowSendModal(true);
  };

  const handleSent = (updated: Quotation) => {
    setQuotations((prev) =>
      prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
    );
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setStartDate(daysAgoLocalIso(90));
    setEndDate(todayLocalIso());
    setPage(1);
  };

  const hasActiveFilters =
    Boolean(searchQuery) ||
    statusFilter !== 'all' ||
    startDate !== daysAgoLocalIso(90) ||
    endDate !== todayLocalIso();

  const columnTotals = useMemo(() => computeQuotationTotals(quotations), [quotations]);

  const visibleRowKeys = useMemo(
    () => quotations.map(quotationRowKey),
    [quotations]
  );

  const selectedRows = useMemo(
    () => quotations.filter((q) => selectedKeys.has(quotationRowKey(q))),
    [quotations, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeQuotationTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [statusFilter, startDate, endDate, searchQuery, page, perPage]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : columnTotals;
  const totalsBarPageSuffix =
    meta && meta.total > columnTotals.count ? ' on this page' : '';
  const showTotalsBar = quotations.length > 0;

  if (loading && quotations.length === 0) {
    return (
      <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
        <Sidebar />
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 relative">
          <div className="flex flex-1 items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 relative">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Quotations</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Sales quotes — informational only, no ledger or inventory impact
                </p>
              </div>
              {canManageQuotations() && (
                <button
                  type="button"
                  onClick={() => navigate('/quotations/manage')}
                  className="flex items-center justify-center px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Quotation
                </button>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchQuotations(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          {/* Status tabs */}
          <div className="mb-4 flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setStatusFilter(tab.id);
                  setPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === tab.id
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search / filter bar */}
          <form onSubmit={handleSearch} className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Quotation #, customer, memo..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quote date from
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quote date to
                </label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700"
              >
                Search
              </button>
              {hasActiveFilters && (
                <>
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {meta?.total ?? quotations.length} quotations
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-sm text-slate-700 underline"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </form>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table className="w-full min-w-[800px] border-separate border-spacing-0">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <SortableTableHeader
                      label="Date"
                      column="quote_date"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Quotation #"
                      column="quotation_number"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Valid Until
                    </th>
                    <SortableTableHeader
                      label="Total"
                      column="total_amount"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortableTableHeader
                      label="Status"
                      column="status"
                      currentSort={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {quotations.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <FileText className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">No quotations found.</p>
                        {canManageQuotations() && (
                          <button
                            type="button"
                            onClick={() => navigate('/quotations/manage')}
                            className="mt-3 text-sm text-slate-700 underline"
                          >
                            Create the first quotation
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    quotations.map((q) => {
                      const busy = actionLoading === q.id;
                      const rowKey = quotationRowKey(q);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                        <tr
                          key={rowKey}
                          className={`hover:bg-gray-50 cursor-pointer${
                            isCheckboxSelected ? ' bg-slate-50/60' : ''
                          }`}
                          onClick={() => navigate(openQuotation(q.id))}
                        >
                          <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isCheckboxSelected}
                              onChange={(e) => toggleRow(rowKey, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-slate-800"
                              aria-label={`Select ${q.quotation_number}`}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {formatDate(q.quote_date)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {q.quotation_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {getQuotationCustomerName(q)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {q.valid_until ? formatDate(q.valid_until) : '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                            {formatCurrency(q.total_amount)}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(q.status)}`}
                            >
                              {formatLabel(q.status)}
                            </span>
                          </td>
                          <td
                            className="px-4 py-4 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <RowActionsMenu
                              {...buildQuotationRowActions(q, {
                                onOpen: () => navigate(openQuotation(q.id)),
                                onConvertToInvoice: () => void handleConvertToInvoice(q),
                                onPrint: () => void handlePrint(q),
                                onDownloadPdf: () => void handleDownloadPdf(q),
                                onSend: () => openSendModal(q),
                                onCopy: () => void handleCopy(q),
                                onDelete: () => void handleDelete(q),
                              })}
                              disabled={busy}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {meta && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <label htmlFor="quotations-per-page" className="text-gray-700">
                  Rows per page
                </label>
                <select
                  id="quotations-per-page"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <span>
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </span>
              {meta.last_page > 1 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchQuotations(meta.current_page - 1)}
                    disabled={meta.current_page <= 1 || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchQuotations(meta.current_page + 1)}
                    disabled={meta.current_page >= meta.last_page || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <TransactionListTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              totalAmount={totalsBarData.totalAmount}
              balance={0}
              hideBalance
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>
      </main>

      {sendQuotationRow && (
        <QuotationSendModal
          quotation={sendQuotationRow}
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSendQuotationRow(null);
          }}
          onSent={handleSent}
        />
      )}
    </div>
  );
};

export default Quotations;
